// src/features/shared-scope/components/SharedLinkDetailPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Right-Panel Detail View — shows full parameterization of a selected link
// + recreate action + revoke action.
//
// Pure renderer — all state & mutations come from the parent hook.
// PAC Glass design system — dark glassmorphism, transparent blur surfaces.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import type { ActivityLogEntry } from '../api/sharedScopeApi';
import { timeAgo } from '@/utils/date';

// ── Status badge ─────────────────────────────────────────────────────────

function statusBadgeClass(status: string): string {
  if (status === 'active')  return 'text-[#39FF14]';
  if (status === 'revoked') return 'text-[#FF3366]';
  if (status === 'expired') return 'text-[#FFD700]';
  return 'text-[#D098EF]';
}

// ── Props ────────────────────────────────────────────────────────────────

interface Props {
  entry:        ActivityLogEntry | null;
  onRevoke:     (token: string) => void;
  onRecreate:   (entry: ActivityLogEntry) => void;
  isRevoking:   boolean;
  isRecreating: boolean;
  recreatedUrl: string | null;
}

// ── Detail Row ───────────────────────────────────────────────────────────

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-4 py-1.5 border-b border-[#1A0A2E]/50 last:border-b-0">
      <span className="text-[10px] text-[#6B4D8A] uppercase tracking-wider whitespace-nowrap pt-0.5">
        {label}
      </span>
      <span className="text-xs text-[#C4A0E0] text-right break-all max-w-[70%]">
        {children}
      </span>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────

export default function SharedLinkDetailPanel({
  entry,
  onRevoke,
  onRecreate,
  isRevoking,
  isRecreating,
  recreatedUrl,
}: Props) {
  const [copied, setCopied] = useState(false);

  if (!entry) {
    return (
      <div className="flex items-center justify-center h-full text-[#3A1F5E] text-sm">
        Select a link to view details
      </div>
    );
  }

  const isActive = entry.status === 'active';
  const scopeDisplay = entry.scope_keys
    ? `${entry.scope_ns} · ${entry.scope_keys.length} keys`
    : `${entry.scope_ns}:${entry.scope_key}`;

  const handleCopyUrl = async () => {
    const url = recreatedUrl || entry.url;
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#1A0A2E] shrink-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-[#FF8C00] truncate">{entry.label || 'Untitled'}</h3>
          <span className={`text-[10px] px-1.5 py-0.5 font-medium ${statusBadgeClass(entry.status)}`}>
            {entry.status.toUpperCase()}
          </span>
        </div>
        <div className="text-[10px] text-[#8A6AAF] mt-1">{timeAgo(entry.created_at)}</div>
      </div>

      {/* Detail fields */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-0">
        <DetailRow label="Scope Type">{entry.scope_type}</DetailRow>
        <DetailRow label="Scope">{scopeDisplay}</DetailRow>
        {entry.scope_keys && (
          <DetailRow label="Scope Keys">
            <div className="space-y-0.5">
              {entry.scope_keys.map(k => (
                <div key={k} className="font-mono text-[10px]">{k}</div>
              ))}
            </div>
          </DetailRow>
        )}
        <DetailRow label="Tenant ID">{entry.tenant_id}</DetailRow>
        <DetailRow label="Permissions">{entry.permissions.join(', ')}</DetailRow>
        <DetailRow label="Created By">{entry.created_by}</DetailRow>
        <DetailRow label="Created At">
          {new Date(entry.created_at).toLocaleString()}
        </DetailRow>
        <DetailRow label="Expires At">
          {entry.expires_at ? new Date(entry.expires_at).toLocaleString() : 'Never'}
        </DetailRow>
        <DetailRow label="Access Count">{entry.access_count}</DetailRow>
        <DetailRow label="Unique Visitors">{entry.unique_visitors}</DetailRow>
        <DetailRow label="Last Accessed">
          {entry.last_accessed_at ? timeAgo(entry.last_accessed_at) : 'Never'}
        </DetailRow>
        {entry.viewer_config && (
          <DetailRow label="Viewer Config">
            <pre className="text-[10px] text-[#9A80C0] font-mono whitespace-pre-wrap">
              {JSON.stringify(entry.viewer_config, null, 2)}
            </pre>
          </DetailRow>
        )}
        {entry.url && (
          <DetailRow label="URL">
            <button
              onClick={handleCopyUrl}
              className="text-[#B8A0E0] hover:text-[#B8A0E0]/80 underline underline-offset-2 transition-colors text-left break-all"
            >
              {copied ? '✓ Copied!' : entry.url}
            </button>
          </DetailRow>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-[#1A0A2E] space-y-2 shrink-0">
        {/* Recreate */}
        <button
          onClick={() => onRecreate(entry)}
          disabled={isRecreating}
          className="w-full px-4 py-2 rounded text-sm font-medium text-[#FF8C00]
                     bg-transparent hover:bg-[#FF8C00]/8 border border-[#FF8C00]/30
                     hover:border-[#FF8C00]/60 disabled:opacity-40 disabled:cursor-not-allowed
                     transition-colors"
        >
          {isRecreating ? 'Recreating…' : 'Recreate Link'}
        </button>

        {/* Recreated URL feedback */}
        {recreatedUrl && (
          <div className="rounded-lg border border-[#39FF14]/20 bg-[#08000F] p-3 space-y-1">
            <div className="text-[10px] text-[#39FF14] uppercase tracking-wider">New URL Created</div>
            <div className="text-xs text-[#C4A0E0] font-mono break-all select-all leading-relaxed">
              {recreatedUrl}
            </div>
            <button
              onClick={handleCopyUrl}
              className="w-full px-3 py-1 rounded border border-[#39FF14]/20 text-[10px] text-[#39FF14]
                         hover:border-[#39FF14]/40 transition-colors"
            >
              {copied ? '✓ Copied!' : 'Copy to Clipboard'}
            </button>
          </div>
        )}

        {/* Revoke */}
        {isActive && (
          <button
            onClick={() => onRevoke(entry.token)}
            disabled={isRevoking}
            className="w-full px-4 py-2 rounded text-sm font-medium
                       text-[#D098EF] hover:text-[#E0C0F8] bg-transparent border border-[#D098EF]/25
                       hover:border-[#D098EF]/50 disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors"
          >
            {isRevoking ? 'Revoking…' : 'Revoke Link'}
          </button>
        )}
      </div>
    </div>
  );
}
